export const SUPPORTED_UNITS = [
  "NOS",
  "KG",
  "LTR",
  "METER",
  "ROLL",
  "PIECE",
  "BOX",
  "GRAM",
] as const;

export type Unit = (typeof SUPPORTED_UNITS)[number];

export type UnitOption = {
  unit: string;
  conversionToBaseUnit: number;
  defaultUnit: boolean;
};

export const normalizeUnitOptions = (
  unitOptions: UnitOption[] | undefined,
  baseUnit?: string | null,
): UnitOption[] => {
  const options = (unitOptions || [])
    .filter((option) => option?.unit)
    .map((option) => ({
      unit: option.unit,
      conversionToBaseUnit: Number(option.conversionToBaseUnit) || 1,
      defaultUnit: Boolean(option.defaultUnit),
    }));

  if (options.length > 0) {
    const withBaseUnit =
      baseUnit && !options.some((option) => option.unit === baseUnit)
        ? [
          { unit: baseUnit, conversionToBaseUnit: 1, defaultUnit: false },
          ...options,
        ]
        : options.map((option) => ({
          ...option,
          conversionToBaseUnit:
            option.unit === baseUnit ? 1 : option.conversionToBaseUnit,
        }));
    const defaultIndex = Math.max(
      0,
      withBaseUnit.findIndex((option) => option.defaultUnit),
    );

    return withBaseUnit.map((option, index) => ({
      ...option,
      defaultUnit: index === defaultIndex,
    }));
  }

  return baseUnit
    ? [{ unit: baseUnit, conversionToBaseUnit: 1, defaultUnit: true }]
    : [];
};

export const getDefaultUnitOption = (
  unitOptions: UnitOption[] | undefined,
  baseUnit?: string | null,
) => {
  const options = normalizeUnitOptions(unitOptions, baseUnit);
  return options.find((option) => option.defaultUnit) || options[0];
};

export const getUnitOption = (
  unitOptions: UnitOption[] | undefined,
  selectedUnit?: string | null,
  baseUnit?: string | null,
) => {
  const options = normalizeUnitOptions(unitOptions, baseUnit);
  return (
    options.find((option) => option.unit === selectedUnit) ||
    options.find((option) => option.defaultUnit) ||
    options[0]
  );
};

export const calculateBaseQuantity = (
  quantity: number | string,
  conversionToBaseUnit: number | string,
) => Number(quantity || 0) * Number(conversionToBaseUnit || 1);

export const calculateUnitPrice = (
  baseUnitPrice: number | string,
  conversionToBaseUnit: number | string,
) => Number(baseUnitPrice || 0) / Number(conversionToBaseUnit || 1);
